/* RESPONSIBLE TEAM: team-reporting */

import { Counter } from '@/features/reporting-charts/counter';
import { EmptyState } from '@/features/reporting-charts/empty-state';
import { HeatmapChart } from '@/features/reporting-charts/heatmap-chart';
import { SeriesChart } from '@/features/reporting-charts/series-chart';
import type {
  ChartConfig,
  ChartDataResponse,
  DateRange,
  RenderContext,
} from '@/features/reporting-charts/types';
import { isChartDataEmpty } from '@/features/reporting-charts/utils/is-chart-data-empty';
import { DonutChart } from '@/features/reporting-charts/donut-chart';
import { TableChart } from '@/features/reporting-charts/table-chart';

export interface ChartProps {
  data: ChartDataResponse;
  config: ChartConfig;
  timezone: string;
  locale?: string;
  dateRange?: DateRange;
  height?: number;
  className?: string;
}

export function Chart({
  data,
  config,
  timezone,
  locale = 'en-US',
  dateRange,
  height = 300,
  className,
}: ChartProps) {
  const renderContext: RenderContext = { timezone, locale, dateRange, height };

  if (isChartDataEmpty(data, config.visualizationType)) {
    return <EmptyState />;
  }

  switch (config.visualizationType) {
    case 'counter':
      return (
        <Counter
          data={data}
          config={config}
          renderContext={renderContext}
          dateRange={dateRange}
          className={className}
        />
      );

    case 'bar':
    case 'horizontal-bar':
    case 'line':
    case 'area':
    case 'column-line':
      return (
        <SeriesChart
          data={data}
          config={config}
          renderContext={renderContext}
          className={className}
        />
      );

    case 'tabular':
      return (
        <TableChart
          data={data}
          config={config}
          renderContext={renderContext}
          className={className}
        />
      );

    case 'data-table':
      // TODO: Implement data-table (distinct from tabular)
      return <EmptyState message="Table coming soon" />;

    case 'donut':
      return (
        <DonutChart
          data={data}
          config={config}
          renderContext={renderContext}
          className={className}
        />
      );

    case 'heatmap':
      return (
        <HeatmapChart
          data={data}
          config={config}
          renderContext={renderContext}
          className={className}
        />
      );

    case 'banner':
      // TODO: Implement banner
      return <EmptyState message="Banner coming soon" />;

    default:
      return <EmptyState message="Unsupported chart type" />;
  }
}
